import type {
  BannerDeeplinkIn,
  BannerIn,
  ConditionSpecIn,
} from "../types";

export type DeeplinkSuggestionKind = "match" | "league" | "casino_game";

export type DeeplinkSuggestion = {
  kind: DeeplinkSuggestionKind;
  id: string;
  deeplink: BannerDeeplinkIn;
  /** Result `provider` param — used when auto-applying casino_game with provider_id. */
  provider?: string;
};

const MATCH_CONDITION_FIELDS = new Set(["sr:match:id", "event_id"]);
const LEAGUE_CONDITION_FIELDS = new Set(["sr:tournament:id", "tournament_id"]);

const parseCsvIds = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  }
  return [];
};

/**
 * Three-state ID parse used by `resolveSingleId`:
 * - `undefined` — field unset / empty, so a matching trigger condition may fill in
 * - `string` — exactly one ID
 * - `null` — multiple IDs, so the condition fallback must not run
 */
const singleParamId = (value: unknown): string | null | undefined => {
  const ids = parseCsvIds(value);
  if (ids.length === 0) return undefined;
  if (ids.length === 1) return ids[0];
  return null;
};

const stripProviderPrefix = (gameId: string, provider: unknown): string => {
  if (typeof provider !== "string") return gameId;
  const trimmed = provider.trim();
  if (!trimmed) return gameId;
  const prefix = `${trimmed}:`;
  return gameId.startsWith(prefix) ? gameId.slice(prefix.length) : gameId;
};

const singleEqualsValue = (
  conditions: ConditionSpecIn[] | undefined,
  fields: Set<string>,
): string | undefined => {
  if (!conditions?.length) return undefined;
  const matches = conditions.filter((condition) => {
    if (condition.type !== "event_field_equals") return false;
    const field = condition.params?.field;
    return typeof field === "string" && fields.has(field);
  });
  if (matches.length !== 1) return undefined;
  const value = matches[0].params?.value;
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const resolveSingleId = (
  paramValue: unknown,
  fallback: string | undefined,
): string | undefined => {
  const fromParam = singleParamId(paramValue);
  // `null` means multiple IDs — suppress, including the condition fallback.
  if (fromParam === null) return undefined;
  return fromParam ?? fallback;
};

const optionalSingleId = (value: unknown): string | undefined => {
  const id = singleParamId(value);
  return typeof id === "string" ? id : undefined;
};

const leagueDeeplink = (
  tournamentId: string,
  sportId?: string,
): BannerDeeplinkIn => ({
  screen: "sportsbook_league",
  tournament_id: tournamentId,
  ...(sportId ? { sport_id: sportId } : {}),
});

export const suggestBannerDeeplink = (input: {
  resultParams?: Record<string, unknown>;
  conditions?: ConditionSpecIn[];
}): DeeplinkSuggestion | undefined => {
  const params = input.resultParams ?? {};

  const matchId = resolveSingleId(
    params.promo_match_ids,
    singleEqualsValue(input.conditions, MATCH_CONDITION_FIELDS),
  );
  if (matchId) {
    return {
      kind: "match",
      id: matchId,
      deeplink: { screen: "sportsbook_match", event_id: matchId },
    };
  }

  const tournamentParam = singleParamId(params.promo_tournament_ids);
  const leagueId =
    tournamentParam === null
      ? undefined
      : (tournamentParam ??
        singleEqualsValue(input.conditions, LEAGUE_CONDITION_FIELDS) ??
        optionalSingleId(params.league_id));
  if (leagueId) {
    return {
      kind: "league",
      id: leagueId,
      deeplink: leagueDeeplink(leagueId, optionalSingleId(params.sport_id)),
    };
  }

  // Multi-game free-spin restrictions are ambiguous — do not pick one silently.
  const gameId = resolveSingleId(params.game_ids, undefined);
  if (gameId) {
    const stripped = stripProviderPrefix(gameId, params.provider);
    const provider =
      typeof params.provider === "string" ? params.provider.trim() : "";
    return {
      kind: "casino_game",
      id: stripped,
      deeplink: { screen: "casino_game", game_id: stripped },
      ...(provider ? { provider } : {}),
    };
  }

  return undefined;
};

export const suggestionMatchesDeeplink = (
  suggestion: DeeplinkSuggestion,
  deeplink?: BannerIn["deeplink"],
): boolean => {
  if (!deeplink || typeof deeplink === "string") return false;
  switch (suggestion.kind) {
    case "match":
      return (
        deeplink.screen === "sportsbook_match" &&
        deeplink.event_id === suggestion.deeplink.event_id
      );
    case "league":
      return (
        deeplink.screen === "sportsbook_league" &&
        deeplink.tournament_id === suggestion.deeplink.tournament_id &&
        (deeplink.sport_id ?? "") === (suggestion.deeplink.sport_id ?? "")
      );
    case "casino_game":
      return (
        deeplink.screen === "casino_game" &&
        deeplink.game_id === suggestion.deeplink.game_id
      );
    default: {
      const _exhaustive: never = suggestion.kind;
      return _exhaustive;
    }
  }
};
